import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MaintenanceCostTracker = ({ equipment, maintenanceRecords }) => {
  const [timeRange, setTimeRange] = useState('6months');
  const [viewType, setViewType] = useState('monthly'); // monthly, quarterly, yearly

  const costData = useMemo(() => {
    const now = new Date();
    const months = timeRange === '6months' ? 6 : timeRange === '12months' ? 12 : 24;
    const data = [];

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date?.toISOString()?.slice(0, 7);
      
      const monthRecords = maintenanceRecords?.filter(record => 
        record?.date?.startsWith(monthKey)
      );
      
      const totalCost = monthRecords?.reduce((sum, record) => sum + record?.cost, 0);
      const preventiveCost = monthRecords?.filter(record => record?.type === 'preventive')?.reduce((sum, record) => sum + record?.cost, 0);
      const correctiveCost = monthRecords?.filter(record => record?.type === 'corrective')?.reduce((sum, record) => sum + record?.cost, 0);
      const calibrationCost = monthRecords?.filter(record => record?.type === 'calibration')?.reduce((sum, record) => sum + record?.cost, 0);

      data?.push({
        month: date?.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
        total: totalCost,
        preventive: preventiveCost,
        corrective: correctiveCost,
        calibration: calibrationCost,
        count: monthRecords?.length
      });
    }

    return data;
  }, [maintenanceRecords, timeRange]);

  const departmentCosts = useMemo(() => {
    const costs = {};
    maintenanceRecords?.forEach(record => {
      const equipment_item = equipment?.find(eq => eq?.id === record?.equipmentId);
      const dept = equipment_item?.department || 'Unknown';
      costs[dept] = (costs?.[dept] || 0) + record?.cost;
    });

    return Object.entries(costs)?.map(([department, cost]) => ({
      department,
      cost,
      percentage: (cost / Object.values(costs)?.reduce((a, b) => a + b, 0) * 100)?.toFixed(1)
    }));
  }, [equipment, maintenanceRecords]);

  const equipmentCostRanking = useMemo(() => {
    const costs = {};
    maintenanceRecords?.forEach(record => {
      const equipment_item = equipment?.find(eq => eq?.id === record?.equipmentId);
      if (equipment_item) {
        const key = `${equipment_item?.deviceId} - ${equipment_item?.name}`;
        costs[key] = (costs?.[key] || 0) + record?.cost;
      }
    });

    return Object.entries(costs)?.map(([name, cost]) => ({ name, cost }))?.sort((a, b) => b?.cost - a?.cost)?.slice(0, 10);
  }, [equipment, maintenanceRecords]);

  const totalCosts = useMemo(() => {
    const total = maintenanceRecords?.reduce((sum, record) => sum + record?.cost, 0);
    const thisMonth = maintenanceRecords?.filter(record => record?.date?.startsWith(new Date()?.toISOString()?.slice(0, 7)))?.reduce((sum, record) => sum + record?.cost, 0);
    const lastMonth = maintenanceRecords?.filter(record => {
        const lastMonthDate = new Date();
        lastMonthDate?.setMonth(lastMonthDate?.getMonth() - 1);
        return record?.date?.startsWith(lastMonthDate?.toISOString()?.slice(0, 7));
      })?.reduce((sum, record) => sum + record?.cost, 0);

    const monthlyChange = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth * 100) : 0;

    return {
      total,
      thisMonth,
      lastMonth,
      monthlyChange,
      averageMonthly: total / Math.max(costData?.length, 1)
    };
  }, [maintenanceRecords, costData?.length]);

  const pieColors = ['#1E40AF', '#059669', '#F59E0B', '#DC2626', '#8B5CF6'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR'
    })?.format(value);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Maintenance Cost Tracker</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor equipment maintenance expenses and trends
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
            >
              <option value="6months">Last 6 Months</option>
              <option value="12months">Last 12 Months</option>
              <option value="24months">Last 24 Months</option>
            </select>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconSize={16}
            >
              Export
            </Button>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Cost Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Costs</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(totalCosts?.total)}
                </p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Euro" size={24} className="text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-success/5 rounded-lg p-4 border border-success/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(totalCosts?.thisMonth)}
                </p>
                <p className={`text-xs ${totalCosts?.monthlyChange >= 0 ? 'text-error' : 'text-success'}`}>
                  {totalCosts?.monthlyChange >= 0 ? '+' : ''}{totalCosts?.monthlyChange?.toFixed(1)}% vs last month
                </p>
              </div>
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={24} className="text-success" />
              </div>
            </div>
          </div>

          <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Average</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(totalCosts?.averageMonthly)}
                </p>
              </div>
              <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="BarChart3" size={24} className="text-warning" />
              </div>
            </div>
          </div>

          <div className="bg-secondary/5 rounded-lg p-4 border border-secondary/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold text-foreground">
                  {maintenanceRecords?.length}
                </p>
              </div>
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="FileText" size={24} className="text-secondary" />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Monthly Cost Trend */}
          <div className="bg-muted/20 rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Monthly Cost Trends</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) => `€${(value / 1000)?.toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                    labelStyle={{ color: 'var(--color-foreground)' }}
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="preventive" stackId="a" fill="#059669" name="Preventive" />
                  <Bar dataKey="corrective" stackId="a" fill="#DC2626" name="Corrective" />
                  <Bar dataKey="calibration" stackId="a" fill="#1E40AF" name="Calibration" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Department Cost Distribution */}
          <div className="bg-muted/20 rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4">Cost by Department</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentCosts}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="cost"
                    label={({ department, percentage }) => `${department} (${percentage}%)`}
                  >
                    {departmentCosts?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors?.[index % pieColors?.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Cost']}
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top Cost Equipment */}
        <div className="mt-8 bg-muted/20 rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Highest Maintenance Costs</h3>
          <div className="space-y-3">
            {equipmentCostRanking?.map((item, index) => (
              <div key={item?.name} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-warning text-warning-foreground' :
                    index === 1 ? 'bg-secondary text-secondary-foreground' :
                    index === 2 ? 'bg-accent text-accent-foreground': 'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{item?.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-foreground">{formatCurrency(item?.cost)}</p>
                  <p className="text-xs text-muted-foreground">
                    {((item?.cost / totalCosts?.total) * 100)?.toFixed(1)}% of total
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceCostTracker;